import { supabaseAdmin } from '../supabase.js';

class GroupService {
  constructor(tenantId = 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3') {
    this.tenantId = tenantId;
  }

  // Fetch all group memberships for teachers
  async getGroupMemberships() {
    try {
      const { data, error } = await supabaseAdmin
        .from('teacher_assignments')
        .select(`
          teacher_id,
          assignment_type,
          member_role,
          committee_name,
          saas_teachers!teacher_assignments_teacher_id_fkey(
            id,
            name,
            teacher_id,
            subject_specialty
          )
        `)
        .eq('tenant_id', this.tenantId)
        .eq('assignment_type', 'committee_member');

      if (error) {
        console.error('Error fetching group memberships:', error);
        return { groups: [], error };
      }

      // Organize data by groups
      const groups = this.organizeGroupsByCategory(data || []);
      return { groups, error: null };
    } catch (error) {
      console.error('Error in getGroupMemberships:', error);
      return { groups: [], error };
    }
  }

  // Organize groups by categories
  organizeGroupsByCategory(assignments) {
    const categories = {
      'administration': {
        name: '行政及管理 Administration and Management',
        groups: [
          { id: 1, name: 'Core Team', chineseName: '核心領導組' },
          { id: 2, name: 'Crisis Management Committee', chineseName: '危機管理組' },
          { id: 3, name: 'School Administrative Board', chineseName: '行政管理組' },
          { id: 4, name: 'National Security Education Committee', chineseName: '國家安全教育組' },
          { id: 5, name: 'School Development Committee', chineseName: '學校發展組' }
        ]
      },
      'learning_teaching': {
        name: '學與教 Learning and Teaching',
        groups: [
          { id: 6, name: 'Academic Committee', chineseName: '教務組' },
          { id: 7, name: 'STEM+ Committee', chineseName: 'STEM+' },
          { id: 8, name: 'Scholarship Committee', chineseName: '獎學金組' },
          { id: 9, name: 'Curriculum Development Committee', chineseName: '課程發展組' },
          { id: 10, name: 'Self-evaluation and Data Analysis Committee', chineseName: '自評及數據分析組' },
          { id: 11, name: 'Information Technology Committee', chineseName: '資訊科技組' },
          { id: 12, name: 'Reading Promotion Committee', chineseName: '閱讀推廣組' },
          { id: 13, name: 'Career Guidance Committee', chineseName: '升學及就業輔導組' },
          { id: 14, name: 'Learning Support Committee', chineseName: '學習支援組' }
        ]
      },
      'student_growth': {
        name: '學生成長及支援 Students Growth and Support',
        groups: [
          { id: 15, name: 'Positive Education Strategic Development Committee', chineseName: '正向教育策略發展組' },
          { id: 16, name: 'Discipline Committee', chineseName: '訓導組' },
          { id: 17, name: 'Counselling Committee', chineseName: '輔導組' },
          { id: 18, name: 'Civic and Moral Education Committee', chineseName: '公民及品德教育組' },
          { id: 19, name: 'Student Activities Committee', chineseName: '學生活動組' },
          { id: 20, name: 'Art Development Sub-Committee', chineseName: '藝術發展小組' },
          { id: 21, name: 'Student Affairs Committee', chineseName: '學生事務組' }
        ]
      },
      'external_affairs': {
        name: '對外事務及總務 External Affairs and General Administration',
        groups: [
          { id: 22, name: 'Project WeCan Working Committee', chineseName: '學校起動計劃組' },
          { id: 23, name: 'Parents Affairs Committee', chineseName: '家長教師會事務組' },
          { id: 24, name: 'Alumni Affairs Committee', chineseName: '校友事務組' },
          { id: 25, name: 'Public Relations and Communication Committee', chineseName: '公關及傳訊組' },
          { id: 26, name: 'General Affairs Committee', chineseName: '總務組' },
          { id: 27, name: 'Staff Welfare Committee', chineseName: '教職員福利組' },
          { id: 28, name: 'Teacher Professional Growth Committee', chineseName: '教師專業成長組' },
          { id: 29, name: 'Cotton Blessing Ministry', chineseName: '棉福事工' }
        ]
      }
    };

    // Create a map of committee names to their group info
    const committeeMap = new Map();
    Object.values(categories).forEach(category => {
      category.groups.forEach(group => {
        committeeMap.set(group.chineseName, group);
        committeeMap.set(group.name, group);
      });
    });

    // Organize assignments by committee
    const committeeAssignments = new Map();
    
    assignments.forEach(assignment => {
      if (assignment.saas_teachers && assignment.committee_name) {
        const committeeName = assignment.committee_name;
        
        if (!committeeAssignments.has(committeeName)) {
          committeeAssignments.set(committeeName, []);
        }
        
        committeeAssignments.get(committeeName).push({
          ...assignment.saas_teachers,
          role: assignment.member_role || ''
        });
      }
    });

    // Assign real members to groups based on actual database assignments
    Object.keys(categories).forEach(categoryKey => {
      categories[categoryKey].groups.forEach(group => {
        // Try to find members for this committee
        const members = committeeAssignments.get(group.chineseName) || 
                       committeeAssignments.get(group.name) || [];
        
        // Remove duplicates based on teacher ID
        const uniqueMembers = [];
        const memberIds = new Set();
        
        members.forEach(member => {
          if (!memberIds.has(member.id)) {
            memberIds.add(member.id);
            uniqueMembers.push(member);
          }
        });
        
        group.members = uniqueMembers;
      });
    });

    return categories;
  }

  // Get teachers by specific group
  async getTeachersByGroup(groupId) {
    try {
      const { data, error } = await supabaseAdmin
        .from('teacher_assignments')
        .select(`
          teacher_id,
          saas_teachers!teacher_assignments_teacher_id_fkey(
            id,
            name,
            teacher_id,
            subject_specialty
          )
        `)
        .eq('tenant_id', this.tenantId)
        .eq('assignment_type', 'committee_member');

      if (error) {
        console.error('Error fetching teachers by group:', error);
        return { teachers: [], error };
      }

      const teachers = (data || []).map(item => item.saas_teachers);
      return { teachers, error: null };
    } catch (error) {
      console.error('Error in getTeachersByGroup:', error);
      return { teachers: [], error };
    }
  }
}

export default GroupService;
